
// --- Vue & Template imports ---
import { Component, Vue} from 'vue-property-decorator';

// --- Models ---
import { Driver } from '@/models/Driver.model';
import { Load, LoadEvent, LoadStop } from '@/models/Load.model';
import { MessageDestination } from '@/models/Sms.model';
import { Route } from '@/models/Route.model';
import { User } from '@/models/User.model';
import { Vehicle } from '@/models/Vehicle.model';

// --- Services ---
import AppService from '@/services/app';
import CustomerService from '@/services/customer';
import DriverService from '@/services/driver';
import LoadEventService from '@/services/loadEvent';
import LoadExtraService from '@/services/loadExtra';
import LoadService from '@/services/load';
import MessageService from '@/services/message';
import RouteService from '@/services/route';
import UserService from '@/services/user';
import VehicleService from '@/services/vehicle';

// --- Third Party ---
import dayjs from 'dayjs';
import Hashids from 'hashids';

@Component
export default class MovementListing extends Vue {

  hashids = new Hashids(process.env.VUE_APP_HASHIDS_SALT);

  // used by Filter Area
  selectedSort: string = 'statusId';
  search: string = '';

  // used by No Data Slot
  isLoadingLoads: boolean = true;
  isLoadingVehicles: boolean = true;

  expandDelay: boolean = false;
  loadStops: LoadStop[] = [];
  availableHorses: Vehicle[] = [];
  availableTrailers: Vehicle[] = [];

  // used by Item Dialog for Create/Update
  showItemDialog: boolean = false;
  valid: boolean = false;
  title: string = '';
  clickedItem: Load = Object.assign({}, this.$store.getters['loads/emptyLoad']);
  isPreload: boolean = false;
  rateString: string = '';

  loadDatePicker: boolean = false;
  allCustomerRoutes: Route[] = [];
  toRouteOptions: Route[] = [];
  rateTypeOptions: Route[] = [];
  selectedRoute: Route = {
    id: 0,
    customerId: 0,
    customerName: '',
    customerCode: '',
    typeId: 0,
    type: '',
    from: '',
    to: '',
    rate: 0.00,
  };

  selectedDriver: Driver = Object.assign({}, this.$store.getters['drivers/emptyDriver']);
  selectedHorse: Vehicle = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);
  startKm: number = 0;
  selectedTrailer1: Vehicle = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);
  selectedTrailer2: Vehicle = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);
  disableLinkedTrailer: boolean = false;

  clicked: boolean = false;

  rules = {
    required: (value: string) => !!value || '',
    requiredNumber: (value: number) => value > 0,
    requiredObject: (value: any) => value.id > 0,
    requiredNumberCanBeZero: (value: number) => {
      if ((typeof value) === 'number') {
        return value.toString().length > 0 && value >= 0;
      } else {
        // will do requiredRateStringCanBeZero validation
        return true;
      }
    },
    requiredRateStringCanBeZero: (value: string) => {
      if ((typeof value) === 'string') {
        return !!value && parseFloat(value) >= 0 || '';
      } else {
        // will do requiredNumberCanBeZero validation
        return true;
      }
    },
    currency: (value: string) =>  /^\d{0,8}(?:\.\d{1,2})?$/.test(value) || 'Limited to two decimal places',
    numbersOnly: (value: string) => (/^\d+$/.test(value)) || 'Invalid character',
    maxOdometer: (value: number) => value < 2000000 || 'Invalid',
  };

  // used by Update Load Dialog
  showUpdateLoadDialog: boolean = false;
  updateLoadFormValid: boolean = false; 
  updateLoadClicked: boolean = false;
  availableAndSelectedDrivers: Driver[] = [];

  // used by Offhook Trailer Dialog
  showOffhookDialog: boolean = false;
  offhookDialogTitle: string = '';
  offhookFormValid: boolean = false; 
  offhookTime: string = dayjs().format('YYYY-MM-DD HH:mm');
  offhookClicked: boolean = false;

  // used by Change Horse Dialog
  showChangeHorseDialog: boolean = false;
  changeHorseDialogTitle: string = '';
  changeHorseFormValid: boolean = false;
  changeHorseClicked: boolean = false;

  // used by Update Trailers Dialog
  showUpdateTrailersDialog: boolean = false;
  updateTrailersFormValid: boolean = false;
  updateTrailersClicked: boolean = false;

  // used by Confirm Action Dialog for Status Update
  action: string = '';
  dialogContent: string = '';
  confirmActionDelay: boolean = false;
  showConfirmActionDialog: boolean = false;
  nextStatusId: number = this.$store.getters['app/statusHelper'].waitOpprove;
  expectedRouteKm: number = 0;
  endKm: number = 0;
  availableLoadStops: LoadStop[] = [];
  offloadId: number = 0;

  // used by Load Event Dialog
  showLoadEventDialog: boolean = false;
  loadEventDialogTitle: string = '';
  loadEvents: LoadEvent[] = [];
  commentFormValid: boolean = false;
  newComment: string = '';
  newCommentIsCritical: boolean = false;
  addCommentClicked: boolean = false;
  criticalClicked: boolean = false;

  // used by Add Extra Dialog
  showAddExtraDialog: boolean = false;
  addExtraDialogTitle: string = '';
  addExtraFormValid: boolean = false;
  extraTypeId: number = 0;
  extraCost: string = '0.00';
  addExtraClicked: boolean = false;

  // used by Message Driver/Vehicle Dialog
  showMessageDialog: boolean = false;
  messageFormValid: boolean = false;
  messageDestinations: MessageDestination[] = [];
  selectedDestinations: MessageDestination[] = [];
  message: string = '';
  sendMessageClicked: boolean = false;
  
  // used by Add Offload Dialog
  showAddOffloadDialog: boolean = false;
  addOffloadDialogTitle: string = '';
  newLoadStops: LoadStop[] = [];
  newOffload: LoadStop = Object.assign({}, this.$store.getters['loads/emptyLoadStop']);
  offloadTimePicker: boolean = false;
  offloadAppointmentTimeTab: string = '';
  offloadAppointmentDate: string = '';
  offloadAppointmentTime: string = '';
  offloadFormValid: boolean = false;
  loadStopCounter: number = 0;
  addOffloadsClicked: boolean = false;
  saveOffloadsFailed: boolean = false;

  // #region Functions to display list data on page load
    sortOptions() {
      return [
        {
          text: 'Priority',
          value: 'priority',
        },
        {
          text: 'Status',
          value: 'statusId',
        },
        {
          text: 'Horse',
          value: 'horseVehicleCode',
        },
        {
          text: 'Trailer',
          value: 'trailer1VehicleCode',
        },
        {
          text: 'Linked Trailer',
          value: 'trailer2VehicleCode',
        },
        {
          text: 'Client',
          value: 'customerCode',
        },
        {
          text: 'Most Recent First',
          value: 'loadDate-newest',
        },
        {
          text: 'Oldest First',
          value: 'loadDate-oldest',
        },
        {
          text: 'From',
          value: 'from',
        },
        {
          text: 'To',
          value: 'to',
        },
      ]
    }

    headers() {
      return [
        {
          sortable: false,
          text: '',
          value: 'status',
        },
        {
          sortable: false,
          text: 'Vehicle',
          value: 'vehicleCodes',
        },
        {
          sortable: false,
          text: 'Client',
          value: 'customerCode',
        },
        {
          sortable: false,
          text: '#',
          value: 'id',
        },
        {
          sortable: false,
          text: 'Date',
          value: 'loadDate',
        },
        {
          sortable: false,
          text: 'From',
          value: 'from',
        },
        {
          sortable: false,
          text: 'To',
          value: 'to',
        },
        {
          sortable: false,
          text: '',
          value: 'criticalComment',
        },
      ];
    }

    customFilter(column: string, search: string, item: Load) {
      let result: boolean = false;
      
      if (search === '') {
        result = true;
      } else {
        //NOTE: Now that we have done the Offloaded Vehicles HACK, we need to exclude offloaded 

        result = item.statusName.toLowerCase().includes(search.toLowerCase()) ||
          (item.horseVehicleCode ? item.horseVehicleCode.toLowerCase().includes(search.toLowerCase()) : false) ||
          (item.trailer1VehicleCode ? item.trailer1VehicleCode.toLowerCase().includes(search.toLowerCase()) : false) ||
          (item.trailer2VehicleCode ? item.trailer2VehicleCode.toLowerCase().includes(search.toLowerCase()) : false) ||
          (item.statusId < 45 ? ('' + item.id).includes(search) : false) ||
          (item.statusId < 45 ? item.customerCode.toLowerCase().includes(search.toLowerCase()) : false) ||
          (item.statusId < 45 ? item.from.toLowerCase().includes(search.toLowerCase()) : false) ||
          (item.statusId < 45 ? item.to.toLowerCase().includes(search.toLowerCase()) : false) ||
          (item.loadDate ? this.getFormattedLoadDate(item.loadDate).toLowerCase().includes(search.toLowerCase()) : false) ||
          (item.statusId < 45 ? item.operatorName.toLowerCase().includes(search.toLowerCase()) : false) ||
          (item.driverNickname ? item.driverNickname.toLowerCase().includes(search.toLowerCase()) : false);
      }

      return result;
    }

    customSort(items: any[], sortBy: string[], sortDesc: boolean[], locale: string) {
      // If the result is negative, a is sorted before b.
      // If the result is positive, b is sorted before a.
      // If the result is 0, no changes are done with the sort order of the two values.

      //NOTE: Now that we have done the Offloaded Vehicles HACK, we need to exclude offloaded 

      switch (sortBy[0]) {
        case 'priority':
          // isPriorityLoad is a boolean
          items.sort((a: Load, b: Load) => b.statusId < 45 ? (b.isPriorityLoad ? '1' : '0').localeCompare((a.isPriorityLoad ? '1' : '0')) : -1);
          break;

        case 'statusId':
          // statusId is a number
          items.sort((a: Load, b: Load) => (b.statusId === 45 ? a.statusId - b.statusId : b.statusId - a.statusId));
          break;

        case 'horseVehicleCode':
          items.sort((a: Load, b: Load) => (a.horseVehicleCode ? a.horseVehicleCode : 'ZZZ999').toLowerCase().localeCompare((b.horseVehicleCode ? b.horseVehicleCode : 'ZZZ999').toLowerCase()));
          break;

        case 'trailer1VehicleCode':
          items.sort((a: Load, b: Load) => b.statusId < 45 ? (a.trailer1VehicleCode ? a.trailer1VehicleCode : 'ZZZ999').toLowerCase().localeCompare((b.trailer1VehicleCode ? b.trailer1VehicleCode : 'ZZZ999').toLowerCase()) : -1);
          break;

        case 'trailer2VehicleCode':
          items.sort((a: Load, b: Load) => b.statusId < 45 ? (a.trailer2VehicleCode ? a.trailer2VehicleCode : 'ZZZ999').toLowerCase().localeCompare((b.trailer2VehicleCode ? b.trailer2VehicleCode : 'ZZZ999').toLowerCase()) : -1);
          break;

        case 'customerCode':
          items.sort((a: Load, b: Load) => b.statusId < 45 ? a.customerCode.toLowerCase().localeCompare(b.customerCode.toLowerCase()) : -1);
          break;

        case 'loadDate-newest':          
          items.sort((a: Load, b: Load) => (b.loadDate ? b.loadDate : '').toLowerCase().localeCompare((a.loadDate ? a.loadDate : '').toLowerCase()));
          break;

        case 'loadDate-oldest':
          items.sort((a: Load, b: Load) => b.statusId < 45 ? (a.loadDate ? a.loadDate : '').toLowerCase().localeCompare((b.loadDate ? b.loadDate : '').toLowerCase()) : -1);
          break;

        case 'from':
          items.sort((a: Load, b: Load) => b.statusId < 45 ? a.from.localeCompare(b.from) : -1);
          break;

        case 'to':
          items.sort((a: Load, b: Load) => b.statusId < 45 ? a.to.localeCompare(b.to) : -1);
          break;
      }

      return items;
    }

    async beforeMount() {
      await AppService.listStatuses(false);
      await VehicleService.listTypes(false);
      await UserService.listAll(false); // to get list of Operators when creating a new load
      await CustomerService.listAll(false); // to get list of Customers when creating a new load
      await RouteService.listAll(false); // to get list of Route Rates when creating a new load
      await DriverService.listActive(true); // to get list of Drivers when viewing loads
      await DriverService.listAvailable(true); // to get list of Available Drivers when creating a new load
      await LoadExtraService.listExtraTypes(true); // to get list of Extra Types when adding an Extra      

      await this.listMovementLoads();
      await this.listAvailableVehicles();

      // #39 Pre-select logged in user if they are an 4: Operator
      /*
      // #130 Remove Operator filter
      if (this.$store.getters['users/loggedInUser'].typeId === 4) {
        this.operatorFilter = this.$store.getters['users/loggedInUser'].displayName;
      }
      */
    }

    getOneWeekPast() {
      return dayjs().add(-1, 'week').format('YYYY-MM-DD');
    }

    getOneWeekFuture() {
      return dayjs().add(1, 'week').format('YYYY-MM-DD');
    }
    
    async refresh() {
      // reset store to empty
      this.$store.dispatch('loads/setMovementLoads', {
        loads: [],
      });
      await this.listMovementLoads();
    }

    async listMovementLoads() {
      try {
        this.isLoadingLoads = true;
        const response: string = await LoadService.listMovementLoads(true);

        if (response.length > 0 && response !== 'No Data Found') {
          AppService.errorHandler(response);
        }

      } finally {
        this.isLoadingLoads = false;
      }
    }

    async listAvailableVehicles() {
      try {
        this.isLoadingVehicles = true;
        const response: string = await VehicleService.listActive(true);

        if (response.length > 0 && response !== 'No Data Found') {
          AppService.errorHandler(response);
          this.availableHorses = [];
        } else {
          this.availableHorses = this.$store.getters['vehicles/offloadedVehiclesByType']('Horse');
          this.availableHorses = this.availableHorses.filter((item: Vehicle) => !item.suspended); // hide the suspended vehicles on this screen
          this.availableTrailers = this.$store.getters['vehicles/offloadedVehiclesByType']('Trailer');
          this.availableTrailers = this.availableTrailers.filter((item: Vehicle) => !item.suspended); // hide the suspended vehicles on this screen
          this.availableTrailers = this.availableTrailers.filter((item: Vehicle) => !item.isLinked || (item.isLinked && item.isFront) ); // hide the rear links 
        }

      } finally {
        this.isLoadingVehicles = false;
      }
    }

    async listOffloads(load: Load) {
      try {
        this.loadStops = [];
        const response = await LoadService.listOffloadsByLoad({ params: { loadId: load.id }});

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
            this.loadStops = [];
          } else {
            this.loadStops = response.data;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        this.loadStops = [];
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Movement.vue - listOffloads - 3396 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      }
    }

    async expandLoad(item: Load, expand: any) {
      this.loadStops = [];
      if (item.offloadCount && item.offloadCount > 0) {
        // go fetch the load stops for this load
        try {
          this.expandDelay = true;
          await this.listOffloads(item);
        } finally {
          this.expandDelay = false;
        }
      }

      expand(true);
    }

    closeRow(expand: any) {
      expand(false);
    }

    getFormattedLoadDate(loadDate: string) {
      return dayjs(loadDate).format('ddd DD MMM YYYY');
    }

    showDriverWarning(type: string, item: Driver) {
      if (!item.deletedAt) {
        switch (type) {
          case 'pdp':
            const pdpExpiryDate = dayjs(item.pdpExpiry);

            if (dayjs().isAfter(pdpExpiryDate)) {
              return 'overdue';
            } else if (dayjs().isAfter(pdpExpiryDate.subtract(21, 'days'))) {
              return 'pdp-due';
            }
        }
      }
      return '';
    }

    viewLoadDetails(item: Load) {
      this.$router.push({name: 'View Load', params: {id: this.hashids.encode(item.id)}});
    }

    allowUpdatePriorityFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 2},
        { type: 'Tracking User', level: 1},
      ]);
    }

    allowAddNewFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 2},
        { type: 'Tracking User', level: 1},
      ]);
    }

    allowAddCommentFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Administrator', level: 2},
        { type: 'Operator', level: 2},
        { type: 'Tracking User', level: 1},
      ]);
    }

    allowMakeCommentCriticalFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 2},
        { type: 'Tracking User', level: 1},
      ]);
    }

    allowMessagingFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 2},
        { type: 'Tracking User', level: 1},
      ]);
    }

    allowAddExtraFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 2},
        { type: 'Tracking User', level: 1},
      ]);
    }

    allowOffhookTrailerFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 2},
        { type: 'Tracking User', level: 1},
      ]);
    }

    allowChangeHorseFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 2},
        { type: 'Tracking User', level: 1},
      ]);
    }

    allowUpdateTrailersFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 2},
      ]);
    }

    allowUpdateLoadFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 2},
        { type: 'Tracking User', level: 1},
      ]);
    }

    allowUpdateStatusFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 2},
        { type: 'Tracking User', level: 1},
      ]);
    }

    allowDeleteFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 1},
      ]);
    }

    allowAddOffloadStopsFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 2},
        { type: 'Tracking User', level: 1},
      ]);
    }

    async togglePriority(item: Load, priority: boolean) {
      try {
        this.clicked = true;

        const response = await LoadService.updatePriority({
          id: item.id,
          isPriorityLoad: priority,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listMovementLoads();
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Movement.vue - togglePriority - 3564 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.clicked = false;
      }
    }
  // #endregion

  // #region Functions required for the Item Dialog for Create
    getRoutesByCustomer(clearRate: boolean) {
      // clear other linked data
      this.toRouteOptions = [];
      this.rateTypeOptions = [];
      if (clearRate) {
        this.clickedItem.rate = 0;
        this.rateString = this.clickedItem.rate.toFixed(2);
      }

      // get the list to populate your FROM drop-down based on ALL Routes for the selected Customer
      this.allCustomerRoutes = this.$store.getters['routes/routes'].filter((item: Route) => item.customerId === this.clickedItem.customerId);
      this.allCustomerRoutes.sort((a: Route, b: Route) => (a.from.toUpperCase() > b.from.toUpperCase()) ? 1 : ((b.from.toUpperCase() > a.from.toUpperCase()) ? -1 : 0));
    }

    getToRouteOptions() {
      // clear other linked data
      this.rateTypeOptions = [];
      this.clickedItem.rate = 0;
      this.rateString = this.clickedItem.rate.toFixed(2);

      // get the list to populate your TO drop-down based on the Routes that have the selected FROM 
      this.toRouteOptions = this.allCustomerRoutes.filter((item: Route) => item.from.toUpperCase() === this.clickedItem.from.toUpperCase());
      this.toRouteOptions.sort((a: Route, b: Route) => (a.to.toUpperCase() > b.to.toUpperCase()) ? 1 : ((b.to.toUpperCase() > a.to.toUpperCase()) ? -1 : 0));
    }

    getRateTypes() {
      // clear other linked data
      this.clickedItem.rate = 0;
      this.rateString = this.clickedItem.rate.toFixed(2);

      this.rateTypeOptions = this.toRouteOptions.filter((item: Route) => item.to.toUpperCase() === this.clickedItem.to.toUpperCase());
      this.rateTypeOptions.sort((a: Route, b: Route) => (a.type.toUpperCase() > b.type.toUpperCase()) ? 1 : ((b.type.toUpperCase() > a.type.toUpperCase()) ? -1 : 0));

      if (this.rateTypeOptions.length === 1) {
        this.selectedRoute = this.rateTypeOptions[0];
        this.setSelectedRate();
      }
    }

    setSelectedRate() {
      this.clickedItem.rate = this.selectedRoute.rate;
      this.rateString = this.clickedItem.rate.toFixed(2);
    }

    selectHorse(selectedVehicle?: Vehicle) {
      if (selectedVehicle) {
        this.selectedHorse = selectedVehicle;
        this.startKm = this.selectedHorse.odometer;
      } else {
        this.startKm = 0;
      }
    }

    selectTrailer1(selectedVehicle?: Vehicle) {
      if (selectedVehicle) {
        this.selectedTrailer1 = selectedVehicle;
        this.selectTrailer2();
        this.disableLinkedTrailer = false;
      }
    }

    getAvailableLinkedTrailers() {
      // reset back to ALL trailers for Trailer2
      let availableRearTrailers = this.$store.getters['vehicles/offloadedVehiclesByType']('Trailer');

      availableRearTrailers = availableRearTrailers.filter((item: Vehicle) => !item.suspended); // hide the suspended vehicles on this screen
      availableRearTrailers = availableRearTrailers.filter((item: Vehicle) => !item.isLinked || (item.isLinked && !item.isFront && item.id === this.selectedTrailer1.linkedTrailerId)  ); // hide the other linked trailers

      if (this.selectedTrailer1.isLinked) {
        this.disableLinkedTrailer = true;
        var index = availableRearTrailers.findIndex((item: Vehicle) => item.id === this.selectedTrailer1.linkedTrailerId);
        if (index >= 0) {
          this.selectTrailer2(availableRearTrailers[index]);
        }
      }

      return availableRearTrailers.filter((item: Vehicle) => item.id !== this.selectedTrailer1.id);
    }

    selectTrailer2(selectedVehicle?: Vehicle) {
      if (selectedVehicle) {
        this.selectedTrailer2 = selectedVehicle;
      } else {
        // was cleared
        this.selectedTrailer2 = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);
      }
    }

    async launchNewItemDialog(item?: Load) {
      this.clickedItem = Object.assign({}, this.$store.getters['loads/emptyLoad']);
      this.clickedItem.loadDate = dayjs().format('YYYY-MM-DD');

      this.selectedRoute = {
        id: 0,
        customerId: 0,
        customerName: '',
        customerCode: '',
        typeId: 0,
        type: '',
        from: '',
        to: '',
        rate: 0.00,
      };

      this.selectedDriver = Object.assign({}, this.$store.getters['drivers/emptyDriver']);
      this.selectedHorse = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);
      this.selectedTrailer1 = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);
      this.selectedTrailer2 = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);

      // just reset these lists in case they have been changed at some point
      await this.listAvailableVehicles();

      this.isPreload = false;
      if (item) {
        this.selectedHorse = this.$store.getters['vehicles/vehicleById'](item.horseVehicleId);
      }

      this.startKm = 0;
      this.selectHorse(this.selectedHorse);

      // #40 If an Operator is selected in the filter, it should default to that Operator
      // Otherwise, if no Operator is selected and the logged in User is an Operator, it should default to them
      /*
      // #130 Remove Operator filter
      if (this.operatorFilter) {
        const selectedOperator: User = this.$store.getters['users/usersByType']('Operator').find((item: User) => this.operatorFilter === item.firstName + ' ' + item.lastName);
        if (selectedOperator) {
          this.clickedItem.operatorId = selectedOperator.id;
        }
      } else 
      */
      if (this.$store.getters['users/loggedInUser'].typeId === 4) {
        const selectLoggedInUser: User = this.$store.getters['users/usersByType']('Operator').find((item: User) => item.isLoggedInUser);
        if (selectLoggedInUser) {
          this.clickedItem.operatorId = selectLoggedInUser.id;
        }
      }

      this.newComment = '';

      this.valid = false;
      this.title = 'Create New Load';

      if (this.$refs.itemForm) {
        (this.$refs.itemForm as any).resetValidation();
      }

      this.showItemDialog = true;
    }

    doCreateUpdate() {
      if (this.clickedItem.id <= 0) {
        this.doCreate();
      } else {
        //this.doUpdate();
      }
    }

    async doCreate() {
      try {
        this.clicked = true;

        let response;

        if (this.isPreload) {
          response = await LoadService.create({
            loadDate: this.clickedItem.loadDate,
            operatorId: this.clickedItem.operatorId,
            orderNumber: this.clickedItem.orderNumber,
            customerId: this.clickedItem.customerId,
            from: this.clickedItem.from,
            to: this.clickedItem.to,
            rate: parseFloat(this.rateString),
            trailer1VehicleId: this.selectedTrailer1.id,
            trailer2VehicleId: (this.selectedTrailer2 ? this.selectedTrailer2.id : undefined),
            isPriorityLoad: this.clickedItem.isPriorityLoad,
            comment: this.newComment,
          });
        } else {
          response = await LoadService.create({
            loadDate: this.clickedItem.loadDate,
            operatorId: this.clickedItem.operatorId,
            orderNumber: this.clickedItem.orderNumber,
            customerId: this.clickedItem.customerId,
            from: this.clickedItem.from,
            to: this.clickedItem.to,
            rate: parseFloat(this.rateString),
            driverId: this.selectedDriver.id,
            horseVehicleId: this.selectedHorse.id,
            startKm: this.startKm,
            trailer1VehicleId: this.selectedTrailer1.id,
            trailer2VehicleId: (this.selectedTrailer2 ? this.selectedTrailer2.id : undefined),
            isPriorityLoad: this.clickedItem.isPriorityLoad,
            comment: this.newComment,
          });
        }

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listMovementLoads();
            await this.listAvailableVehicles();
            await DriverService.listAvailable(true); // to get updated list of Available Drivers
            this.scrollToTop();
            this.showItemDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Movement.vue - doCreate - 3762 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.clicked = false;
      }
    }

    scrollToTop() {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
  // #endregion

  // #region Functions required for the Dialog to Update Load
    hasRouteRate() {
      const index = this.allCustomerRoutes.findIndex((item: Route) => (item.from.toUpperCase() === this.clickedItem.from.toUpperCase()) && (item.to.toUpperCase() === this.clickedItem.to.toUpperCase()) && (item.type.toUpperCase() === this.selectedRoute.type.toUpperCase() ));
      if (index >= 0) {
        return true;
      } else {
        return false;
      }
    }

    useRouteRate() {
      const index = this.allCustomerRoutes.findIndex((item: Route) => (item.from.toUpperCase() === this.clickedItem.from.toUpperCase()) && (item.to.toUpperCase() === this.clickedItem.to.toUpperCase()) && (item.type.toUpperCase() === this.selectedRoute.type.toUpperCase() ));
      if (index >= 0) {
        this.clickedItem.rate = this.allCustomerRoutes[index].rate;
        this.rateString = this.clickedItem.rate.toFixed(2);
      }
    }

    launchUpdateLoadDialog(item: Load) {
      this.clickedItem = Object.assign({}, item);

      this.rateString = this.clickedItem.rate.toFixed(2);
      const origRate = this.clickedItem.rate;

      // clear linked data
      this.toRouteOptions = [];
      this.rateTypeOptions = [];
      this.selectedRoute = {
        id: 0,
        customerId: 0,
        customerName: '',
        customerCode: '',
        typeId: 0,
        type: '',
        from: '',
        to: '',
        rate: 0.00,
      };

      // just to try get the selected Rate Type o_0
      this.getRoutesByCustomer(false);
      this.getToRouteOptions();
      
      this.rateTypeOptions = this.toRouteOptions.filter((item: Route) => item.to.toUpperCase() === this.clickedItem.to.toUpperCase());
      this.rateTypeOptions.sort((a: Route, b: Route) => (a.type.toUpperCase() > b.type.toUpperCase()) ? 1 : ((b.type.toUpperCase() > a.type.toUpperCase()) ? -1 : 0));

      if (this.rateTypeOptions.length === 1) {
        this.selectedRoute = this.rateTypeOptions[0];
        this.setSelectedRate();
      } else {
        // try find the selected rate by the actual rate amount
        const i = this.rateTypeOptions.findIndex((item: Route) => item.rate === origRate)
        if (i >= 0) {
          this.selectedRoute = this.rateTypeOptions[i];
        }
      }
      
      this.clickedItem.rate = origRate;
      this.rateString = this.clickedItem.rate.toFixed(2);

      this.selectedDriver = this.$store.getters['drivers/activeDrivers'].find((item: Driver) => item.id === this.clickedItem.driverId);
      this.availableAndSelectedDrivers = Array.from(this.$store.getters['drivers/availableDrivers']);
      this.availableAndSelectedDrivers.unshift(this.selectedDriver);

      this.updateLoadFormValid = true;

      if (this.$refs.updateLoadForm) {
        (this.$refs.updateLoadForm as any).resetValidation();
      }

      this.showUpdateLoadDialog = true;
    }

    async doUpdateLoad() {
      try {
        this.updateLoadClicked = true;

        const response = await LoadService.updateMovement({ 
          id: this.clickedItem.id,
          orderNumber: this.clickedItem.orderNumber,
          customerId: this.clickedItem.customerId,
          from: this.clickedItem.from,
          to: this.clickedItem.to,
          rate: parseFloat(this.rateString),
          driverId: this.selectedDriver.id,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listMovementLoads();
            this.showUpdateLoadDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {

        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Movement.vue - doUpdateLoad - 3839 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      } finally {
        this.updateLoadClicked = false;
      }
    }    
  // #endregion

  // #region Functions required for the Offhook Trailer Dialog
    async launchOffhookDialog(item: Load) {
      this.clickedItem = Object.assign({}, item);

      this.newComment = '';
      this.clickedItem.endKm = this.clickedItem.startKm;
      this.clickedItem.swapLocation = '';

      this.offhookFormValid = false;
      this.offhookDialogTitle = 'Offhooking Trailer for Load #' + this.clickedItem.id;

      this.$nextTick(() => {
        if (this.$refs.offhookForm) {
          ((this.$refs.offhookForm) as any).resetValidation();
        }
      });

      this.showOffhookDialog = true;
    }

    async offhookTrailer() {
      try {
        this.offhookClicked = true;

        const response = await LoadService.offHookTrailer({ 
          id: this.clickedItem.id,
          swapLocation: this.clickedItem.swapLocation!,
          endKm: this.clickedItem.endKm!,
          comment: this.newComment,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listMovementLoads();
            await this.listAvailableVehicles();
            await DriverService.listAvailable(true); // to get updated list of Available Drivers
            this.showOffhookDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {

        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Movement.vue - offhookTrailer - 3899 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      } finally {
        this.offhookClicked = false;
      }
    }
  // #endregion

  // #region Functions required for the Change Horse Dialog
    async launchChangeHorseDialog(item: Load) {
      this.clickedItem = Object.assign({}, item);

      this.clickedItem.oldHorseEndKm = this.clickedItem.startKm;
      if (this.clickedItem.horseVehicleId) {
        this.clickedItem.swapLocation = '';
      }

      this.selectedDriver = Object.assign({}, this.$store.getters['drivers/emptyDriver']);
      this.selectedHorse = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);
      this.startKm = 0;
      
      this.newComment = '';

      // get the latest of this list
      await this.listAvailableVehicles();

      this.changeHorseFormValid = false;
      this.changeHorseDialogTitle = (this.clickedItem.horseVehicleId ? 'Change ' : 'Attach ') + 'Horse for Load #' + item.id;

      this.$nextTick(() => {
        if (this.$refs.changeHorseForm) {
          ((this.$refs.changeHorseForm) as any).resetValidation();
        }
      });

      this.showChangeHorseDialog = true;
    }

    async doChangeHorse() {
      try {
        this.changeHorseClicked = true;

        const response = await LoadService.changeHorse({ 
          id: this.clickedItem.id,
          swapLocation: this.clickedItem.swapLocation!,
          oldHorseEndKm: this.clickedItem.oldHorseEndKm!,
          newHorseVehicleId: this.selectedHorse.id,
          newHorseStartKm: this.startKm,
          newDriverId: this.selectedDriver.id,
          reasonForChange: this.newComment,
          });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listMovementLoads();
            await this.listAvailableVehicles();
            await DriverService.listAvailable(true); // to get updated list of Available Drivers
            this.showChangeHorseDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {

        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Movement.vue - doChangeHorse - 3969 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      } finally {
        this.changeHorseClicked = false;
      }
    }
  // #endregion

  // #region Functions required for the Update Trailers Dialog
    getAvailableLinkedTrailersForUpdate() {
      // reset back to ALL trailers for Trailer2
      let availableRearTrailers = this.$store.getters['vehicles/offloadedVehiclesByType']('Trailer');      

      availableRearTrailers = availableRearTrailers.filter((item: Vehicle) => !item.suspended); // hide the suspended vehicles on this screen
      availableRearTrailers = availableRearTrailers.filter((item: Vehicle) => !item.isLinked || (item.isLinked && !item.isFront && item.id === this.selectedTrailer1.linkedTrailerId)  ); // hide the other linked trailers

      // now we need to quickly add the current trailer2 in as well.
      // ONLY add it if:
      // 1. we have a trailer2VehicleId
      if (this.clickedItem.trailer2VehicleId && this.clickedItem.trailer2VehicleId > 0) {
        let tempSelectedLinkedTrailer = this.$store.getters['vehicles/vehicleById'](this.clickedItem.trailer2VehicleId);
   
        // 2. if this linked trailer is linked and linked to this selectedTrailer 1
        // 3. OR this selected Trailer 1 & 2 are not force linkec
        if ((tempSelectedLinkedTrailer.isLinked && tempSelectedLinkedTrailer.linkedTrailerId === this.selectedTrailer1.id) ||
          (!this.selectedTrailer1.isLinked && !tempSelectedLinkedTrailer.isLinked)) {
          availableRearTrailers.unshift(tempSelectedLinkedTrailer);
          this.selectedTrailer2 = tempSelectedLinkedTrailer;
        }
      }

      if (this.selectedTrailer1.isLinked) {
        this.disableLinkedTrailer = true;
        var index = availableRearTrailers.findIndex((item: Vehicle) => item.id === this.selectedTrailer1.linkedTrailerId);
        if (index >= 0) {
          this.selectTrailer2(availableRearTrailers[index]);
        }
      }

      return availableRearTrailers.filter((item: Vehicle) => item.id !== this.selectedTrailer1.id);
    }

    async launchUpdateTrailersDialog(item: Load) {
      this.clickedItem = Object.assign({}, item);

      // just reset the this.availableTrailers list in case they have been changed at some point
      await this.listAvailableVehicles();

      // now we need to quickly add the current trailers in as well.
      this.selectedTrailer1 = this.$store.getters['vehicles/vehicleById'](this.clickedItem.trailer1VehicleId);
      this.availableTrailers.unshift(this.selectedTrailer1); // we will always have at least 1 trailer

      this.updateTrailersClicked = false;

      this.$nextTick(() => {
        if (this.$refs.updateTrailersForm) {
          ((this.$refs.updateTrailersForm) as any).resetValidation();
        }
      });

      this.showUpdateTrailersDialog = true;
    }

    async doUpdateTrailers() {
      try {
        this.updateTrailersClicked = true;

        const response = await LoadService.updateTrailers({ 
          id: this.clickedItem.id,
          trailer1VehicleId: this.selectedTrailer1.id!,
          trailer2VehicleId: this.selectedTrailer2.id,
          });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listMovementLoads();
            await this.listAvailableVehicles();
            this.showUpdateTrailersDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {

        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Movement.vue - doUpdateTrailers - 3969 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      } finally {
        this.updateTrailersClicked = false;
      }
    }
  // #endregion

  // #region Functions required for the List Load Events / Add Comment Dialog
    async listLoadEvents() {
      try {
        // always check for all the comments in case someone else added one in the background - until we are using websockets
        const response = await LoadEventService.listByLoad({params: {loadId: this.clickedItem.id}});

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            this.loadEvents = [];
          } else {
           this.loadEvents = response.data;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Movement.vue - listLoadEvents - 3999 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      }
    }

    async launchLoadEventDialog(item: Load) {
      this.clickedItem = Object.assign({}, item);
      this.newComment = '';
      this.newCommentIsCritical = false;
      
      // fetch load events associated with this load
      await this.listLoadEvents();

      this.commentFormValid = false;
      this.loadEventDialogTitle = 'Events for Load #' + this.clickedItem.id;

      if (this.$refs.commentForm) {
        (this.$refs.commentForm as any).resetValidation();
      }

      this.showLoadEventDialog = true;
    }

    async markAsCritical(item: LoadEvent, isCritical: boolean) {
      try {
        this.criticalClicked = true;

        const response = await LoadEventService.markAsCritical({
          loadEventId: item.id,
          isCritical: isCritical,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            await this.listMovementLoads();
            await this.listLoadEvents();
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Movement.vue - markAsCritical - 4049 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.criticalClicked = false;
      }
    }

    async createComment() {
      try {
        this.addCommentClicked = true;

        const response = await LoadEventService.createComment({
          loadId: this.clickedItem.id,
          comment: this.newComment,
          isCritical: this.newCommentIsCritical,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listMovementLoads();
            await VehicleService.listActive(true);
            this.showLoadEventDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Movement.vue - createComment - 4086 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.addCommentClicked = false;
      }
    }
  // #endregion

  // #region Functions required for the SMS Dialog
    messageDestinationHeaders() {
      return [
        {
          text: '',
          value: 'name',
        },
        {
          text: '',
          value: 'contact',
        },
      ];
    }

    async launchMessageDialog(item: Load) {
      this.clickedItem = Object.assign({}, item);

      this.message = '';
      this.messageDestinations = [];
      this.selectedDestinations = [];

      this.messageDestinations.push({ 
        id: this.clickedItem.driverId!, 
        isHorse: false, 
        name: this.clickedItem.driverNickname!, 
        contact: this.clickedItem.driverContact!,
      });

      this.messageDestinations.push({ 
        id: this.clickedItem.horseVehicleId!, 
        isHorse: true, 
        name: this.clickedItem.horseVehicleCode!, 
        contact: this.clickedItem.horseVehicleContact!
      });

      this.$nextTick(() => {
        if (this.$refs.messageForm) {
          ((this.$refs.messageForm) as any).resetValidation();
        }
      });

      this.showMessageDialog = true;
    }

    async sendMessage() {
      try {
        this.sendMessageClicked = true;

        const response = await MessageService.create({ 
          destinations: this.selectedDestinations,
          message: this.message,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            this.showMessageDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Movement.vue - sendMessage - 4165 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      } finally {
        this.sendMessageClicked = false;
      }
    }
  // #endregion

  // #region Functions required for the Add Extra Dialog
    async launchAddExtraDialog(item: Load) {
      this.clickedItem = Object.assign({}, item);

      this.extraTypeId = 0;
      this.extraCost = '0.00';
      this.newComment = '';

      this.addExtraDialogTitle = 'Add Extra for Load #' + this.clickedItem.id;

      this.$nextTick(() => {
        if (this.$refs.addExtraForm) {
          ((this.$refs.addExtraForm) as any).resetValidation();
        }
      });

      this.showAddExtraDialog = true;
    }

    async addLoadExtra() {
      try {
        this.addExtraClicked = true;

        const response = await LoadExtraService.createExtraFromMovement({ 
          id: this.clickedItem.id,
          extraTypeId: this.extraTypeId,
          amount: parseFloat(this.extraCost),
          comment: this.newComment,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listMovementLoads();
            this.showAddExtraDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Movement.vue - addLoadExtra - 4221 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      } finally {
        this.addExtraClicked = false;
      }
    }
  // #endregion
  
  // #region Functions required for the Offloads Dialog
    offloadHeaders(showArrived: boolean) {
      let headers: any = [
        {
          text: 'Client',
          value: 'client',
        },
        {
          text: 'Address',
          value: 'address',
        },
        {
          text: 'Appointment Time',
          value: 'appointmentTime',
        },
      ];

      if (showArrived) {
        headers.push({
          text: 'Arrived',
          value: 'arrivedAt',
        });
      }

      headers.push({
          sortable: false,
          text: '',
          value: 'action',
          align: 'end',
        });

      return headers;
    }
    
    launchAddOffloadDialog(item: Load) {
      this.clickedItem = Object.assign({}, item);

      this.addOffloadDialogTitle = 'Add Offload Stop(s) for Load #' + this.clickedItem.id;
      this.loadStopCounter = 0;
      this.newLoadStops = [];
      this.newOffload = Object.assign({}, this.$store.getters['loads/emptyLoadStop']);

      this.$nextTick(() => {
        if (this.$refs.addOffloadForm) {
          ((this.$refs.addOffloadForm) as any).resetValidation();
        }
      });

      this.showAddOffloadDialog = true;
    }

    closeOffloadTimePicker() {
      this.newOffload.appointmentTime = this.offloadAppointmentDate + ' ' + this.offloadAppointmentTime;
      this.offloadTimePicker = false;

      // prevents animate to date tab when closing
      setTimeout(() => (this.offloadAppointmentTimeTab = "date"), 300);
    }

    addNewLoadStop() {
      this.newOffload.id = --this.loadStopCounter;
      this.newLoadStops.push(this.newOffload);

      if (this.newLoadStops.length === 1) {
        this.offloadId = this.newOffload.id;
      }

      this.newOffload = Object.assign({}, this.$store.getters['loads/emptyLoadStop']);

      this.$nextTick(() => {
        if (this.$refs.offloadForm) {
          ((this.$refs.offloadForm) as any).resetValidation();
        }
      });

      this.$nextTick(() => {
        if (this.$refs.addOffloadForm) {
          ((this.$refs.addOffloadForm) as any).resetValidation();
        }
      });
    }

    deleteLoadStop(deleteItem: LoadStop) {
      this.newLoadStops = this.newLoadStops.filter((item: LoadStop) => item.id != deleteItem.id);
    }

    async saveLoadStops() {
      try {
        this.addOffloadsClicked = true;

        const response = await LoadService.createOffloads({ loadId: this.clickedItem.id, offloads: this.newLoadStops });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
            this.saveOffloadsFailed = true;
          } else {
            AppService.successHandler(response.data.message);
            await this.listMovementLoads();
            await this.listAvailableVehicles();
            await this.listOffloads(this.clickedItem);
            this.showConfirmActionDialog = false;
            this.showAddOffloadDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        this.saveOffloadsFailed = true;
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Movement.vue - saveLoadStops - 4336 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      } finally {
        this.addOffloadsClicked = false;
      }
    }
  // #endregion

  // #region Functions required for the Confirm Action Dialog for Status Update
    async launchUpdateStatusDialog(item: Load) {
      this.clickedItem = item;
      this.action = 'CHANGELOADSTATUS';
      this.endKm = 0;
      this.newLoadStops = [];
      this.loadStopCounter = 0;

      this.dialogContent = '';
      switch (this.clickedItem.statusId) {
        case this.$store.getters['app/statusHelper'].new:
          this.dialogContent = ' to <span style="font-weight: 700">Loading</span>.<br/><br/> Are you sure?';
          break;

        case this.$store.getters['app/statusHelper'].loading:
          this.dialogContent = ' to <span style="font-weight: 700">En Route</span>.';                    
          this.newOffload = Object.assign({}, this.$store.getters['loads/emptyLoadStop']);          

          this.$nextTick(() => {
            if (this.$refs.offloadForm) {
              ((this.$refs.offloadForm) as any).resetValidation();
            }
          });
          break;

        case this.$store.getters['app/statusHelper'].enRoute:
          await this.listOffloads(this.clickedItem);
          this.dialogContent = ' to <span style="font-weight: 700">Arrived</span>';
          this.availableLoadStops = this.loadStops.filter((item: LoadStop) => !item.arrivedAt);
          this.offloadId = this.availableLoadStops[0].id;
          break;

        case this.$store.getters['app/statusHelper'].arrived:
          await this.listOffloads(this.clickedItem);          
          this.availableLoadStops = this.loadStops.filter((item: LoadStop) => !item.arrivedAt);
          this.offloadId = this.availableLoadStops.length > 0 ? this.availableLoadStops[0].id : 0;
          this.nextStatusId = this.offloadId > 0 ? this.$store.getters['app/statusHelper'].enRoute : this.$store.getters['app/statusHelper'].waitOpprove;

          // figure out the expectedRouteKms now
          this.getRoutesByCustomer(false);
          this.getToRouteOptions();
          this.getRateTypes();

          this.expectedRouteKm = 0;
          if (this.rateTypeOptions.length > 0) {
            // route kms should be the same regardless of rate type, so just grab it from the first one
            this.expectedRouteKm =  this.rateTypeOptions[0].routeKm!;
          }

          break;
      }

      this.dialogContent = 'You are about to update the state of <span style="font-weight: 700;">Load #' + this.clickedItem.id + '</span> from <span style="font-weight: 700">'
        + this.clickedItem.statusName + '</span>' + this.dialogContent;

      this.showConfirmActionDialog = true;
    }

    async launchDeleteItemDialog(item: Load) {
      this.clickedItem = Object.assign({}, item);

      this.action = 'DELETE';
      this.dialogContent = 'You are about to <span style="font-weight: 700;color: #C01212">delete</span> ' 
        + ' Load #' + this.clickedItem.id + '.<br/><br/> Are you sure?';
      this.showConfirmActionDialog = true;
    }

    ConfirmActionDialog_cancel() {
      this.showConfirmActionDialog = false;
    }

    async ConfirmActionDialog_doConfirmed() {
      if (this.action === 'CHANGELOADSTATUS') {
        await this.doStatusUpdate();
      } else if (this.action === 'DELETE') {
        await this.doDelete();
      }
    }

    async doStatusUpdate() {
      try {
        this.confirmActionDelay = true;

        this.saveOffloadsFailed = false;
        let selectedLoadStop: LoadStop | undefined = Object.assign({}, this.$store.getters['loads/emptyLoad']);
        if (this.newLoadStops.length > 0) {

          // find the Offload for the selected Offload Id that doesn't exist in the DB yet
          selectedLoadStop = this.newLoadStops.find((item: LoadStop) => item.id === this.offloadId);
          await this.saveLoadStops();

          // now go find that one in the proper list!
          await this.listOffloads(this.clickedItem);
          selectedLoadStop = this.loadStops.find((item: LoadStop) => {
            return selectedLoadStop && item.appointmentTime === selectedLoadStop.appointmentTime && 
              item.client === selectedLoadStop.client && 
              item.address === selectedLoadStop.address;
            });
        }

        if (!this.saveOffloadsFailed) {
          const newOffloadId = (selectedLoadStop && selectedLoadStop.id > 0 ? selectedLoadStop.id : this.offloadId);
          const response = await LoadService.updateStatus({ id: this.clickedItem.id, endKm: this.endKm, offloadId: newOffloadId });

          if (response && response.data) {
            if (response.data.result && response.data.result === 'false') {
              AppService.errorHandler(response.data.message);
            } else {
              AppService.successHandler(response.data.message);
              await this.listMovementLoads();
              await this.listAvailableVehicles();
              await DriverService.listAvailable(true); // to get updated list of Available Drivers
              this.showConfirmActionDialog = false;

              if (this.offloadId > 0) {
                await this.listOffloads(this.clickedItem);
              }        
            }
          } else {
            // response is undefined or has no data field - SHOULD NEVER HAPPEN!
            throw new Error('response: ' + JSON.stringify(response));
          }
        }

      } catch (error) {

        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Movement.vue - doStatusUpdate - 4474 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      } finally {
        this.confirmActionDelay = false;
      }
    }

    async doDelete() {
      try {
        this.confirmActionDelay = true;

        const response = await LoadService.delete({
          id: this.clickedItem.id,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listMovementLoads();
              await this.listAvailableVehicles();
              await DriverService.listAvailable(true); // to get updated list of Available Drivers
            this.showConfirmActionDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Movement.vue - doDelete - 4509 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.confirmActionDelay = false;
      }
    }
  // #endregion

}
